// Copyright 2018 Sorama B.V.

import { autoinject, computedFrom, Disposable } from "aurelia-framework";
import { GenericMeasurement, MeasurementPartType, Point3D } from "datatypes/SoramaWebApi";
import { URA, URAProperties } from "libraries/Math/URA";
import { IModal } from "libraries/WorkflowFramework/Interfaces/IModal";
import { IWorkflowModuleModalInstance } from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { MeasurementRepository, MeasurementUpdateField } from "repositories/MeasurementRepository";
import { ChannelSelection } from "services/ChannelSelection/ChannelSelection";
import { IMeasurementProcessorResolver } from "services/Measurement/IMeasurementProcessorResolver";
import { ISpectralProcessingService } from "services/SoundProcessing/ISpectralProcessingService";

@autoinject
export class MeasurementInfoModal implements IModal<MeasurementInfoModalState, void> {
    private modalInstance: IWorkflowModuleModalInstance<MeasurementInfoModalState, void>;

    public currentMeasurement: GenericMeasurement;
    public sensorPositions: Point3D[];
    public channelSelection: ChannelSelection;

    private uraProperties: URAProperties;
    public errorMessage: string;

    @computedFrom("microphoneRmsValues", "microphoneRmsMinMax")
    public get microphoneValues(): number[] {
        if (!this.microphoneRmsValues || !this.microphoneRmsMinMax) return null;

        return this.microphoneRmsValues.map(
            (x) => (x - this.microphoneRmsMinMax.min) / (this.microphoneRmsMinMax.max - this.microphoneRmsMinMax.min)
        );
    }

    private microphoneRmsMinMax: { min: number; max: number };
    private microphoneRmsValues: number[];
    private initialSelection: boolean[];
    private maximumMutedSensors: number;

    public changed = false;
    public applied = true;
    public empty = true;
    public interpolationWarning = false;
    public mutedMicError = false;
    public busy: boolean;
    public statusMessage: string;
    public addMetadata: boolean;
    public newProperty: string;

    private channelSelectionObserver: Disposable;
    private activatePromise: Promise<void> = Promise.resolve();
    private refreshMeasurements: () => void;

    private metadata: { key: string; value: string }[] = [];

    constructor(
        private readonly measurementRepository: MeasurementRepository,
        private readonly measurementProcessorResolver: IMeasurementProcessorResolver
    ) {}

    public activateModal(
        state: MeasurementInfoModalState,
        modalInstance: IWorkflowModuleModalInstance<MeasurementInfoModalState, void>
    ) {
        this.modalInstance = modalInstance;
        this.currentMeasurement = state.measurement;
        this.refreshMeasurements = state.refreshMeasurements;

        this.modalInstance = modalInstance;

        this.addMetadata = false;
        this.newProperty = "";
        this.busy = true;

        // Load entire measurement for mic positions
        this.activatePromise = this.measurementRepository
            .getMeasurement(state.measurement.id)
            .then((x) => {
                // Set the measurement info to the latest measurement info to be as much up to date as possible
                this.currentMeasurement = x;

                this.metadata = x.metadata ? Object.entries(x.metadata).map(([key, value]) => ({ key, value })) : [];
            })
            .then(async () => {
                if (this.currentMeasurement.isComputedMeasurement) return;

                this.sensorPositions = this.currentMeasurement.parts
                    .filter((x) => x.measurementType === MeasurementPartType.Audio)
                    .map((x) =>
                        x.device.sensorPositions.map((y) => ({
                            x: y.x + x.position.x,
                            y: y.y + x.position.y,
                            z: y.z + x.position.z,
                        }))
                    )
                    .reduce((p, c) => p.concat(c));

                // Make sure the user mutes no more than 25% of the sensors
                this.maximumMutedSensors = this.sensorPositions.length / 4;
                this.uraProperties = URA.getUraProperties(this.sensorPositions.map((x) => x));

                const selection = this.sensorPositions.map((a) =>
                    this.currentMeasurement.mutedSensors
                        ? this.currentMeasurement.mutedSensors.findIndex((b) =>
                              this.equals({ x: a.x, y: a.y, z: a.z }, b)
                          ) !== -1
                        : false
                );

                this.initialSelection = selection;
                this.channelSelection = new ChannelSelection(selection.map((x) => x));
                this.channelSelectionObserver = this.channelSelection.changeObserver(this.onChannelSelectionChanged);

                this.updateMicrophoneChangedStatus();
                this.updateMicrophoneInterpolationWarningStatus();

                // Load measurement rms values, but do not return the promise
                this.measurementProcessorResolver
                    .resolveProcessor<ISpectralProcessingService>(ISpectralProcessingService, this.currentMeasurement)
                    .then((processor) => {
                        return processor.getSensorRMSValues(this.currentMeasurement).then((x) => {
                            this.microphoneRmsValues = x;
                            this.updateMicrophoneRmsMinMax();
                        });
                    })
                    .catch((e) => {
                        // An error here does not break functionality, do not bother the user with it, but log the error in the console
                        console.error(e);
                    });
            })
            .catch((e) => {
                console.error(e);
            })
            .finally(() => {
                this.busy = false;
                this.statusMessage = undefined;
            });
    }

    public deactivate(): void {
        this.activatePromise.then(() => {
            if (this.channelSelectionObserver) {
                this.channelSelectionObserver.dispose();
                this.channelSelectionObserver = undefined;
            }
        });
    }

    public apply(): void {
        this.activatePromise = this.activatePromise.then(() => {
            // Update muted mics
            const mutedMics = this.channelSelection
                .getSelection()
                .map((x, i) =>
                    x
                        ? {
                              x: this.sensorPositions[i].x,
                              y: this.sensorPositions[i].y,
                              z: this.sensorPositions[i].z,
                          }
                        : undefined
                )
                .filter((x) => x);

            return this.measurementRepository
                .updateMeasurement(this.currentMeasurement, MeasurementUpdateField.mutedSensors, mutedMics)
                .then(() => {
                    this.applied = true;
                    this.mutedMicError = false;
                    if (this.refreshMeasurements) {
                        this.refreshMeasurements();
                    }
                })
                .catch((e) => {
                    console.error(e);
                    this.mutedMicError = true;
                });
        });
    }

    public resetMutedMicrophones(): void {
        this.activatePromise = this.activatePromise.then(() => {
            if (this.channelSelectionObserver) {
                this.channelSelectionObserver.dispose();
                this.channelSelectionObserver = undefined;
            }

            this.channelSelection = new ChannelSelection(this.initialSelection.map((x) => x));
            this.channelSelectionObserver = this.channelSelection.changeObserver(this.onChannelSelectionChanged);
            this.changed = false;
            this.onChannelSelectionChanged();
        });
    }

    private onChannelSelectionChanged = (index?: number): void => {
        if (index && this.channelSelection.selectedCount > this.maximumMutedSensors) {
            this.channelSelection.toggle(index);
            return;
        }

        this.activatePromise = this.activatePromise.then(() => {
            this.updateMicrophoneChangedStatus();
            this.updateMicrophoneInterpolationWarningStatus();
            this.updateMicrophoneRmsMinMax();

            // Update muted mics
            const mutedMics = this.channelSelection
                .getSelection()
                .map((x, i) => (x ? this.sensorPositions[i] : undefined))
                .filter((x) => x);

            // Update muted microphones in measurement
            if (
                (!mutedMics || mutedMics.length === 0) &&
                (!this.currentMeasurement.mutedSensors || this.currentMeasurement.mutedSensors.length === 0)
            ) {
                // No selection
                this.applied = true;
                return;
            } else if (
                this.currentMeasurement.mutedSensors &&
                mutedMics.length === this.currentMeasurement.mutedSensors.length &&
                mutedMics.every((x) => this.currentMeasurement.mutedSensors.some((y) => this.equals(x, y)))
            ) {
                // No change
                this.applied = true;
                return;
            }

            this.applied = false;
        });
    };

    private updateMicrophoneChangedStatus(): void {
        // Check if there are any changes
        let changed = false;
        for (let i = 0; i < this.initialSelection.length; i++) {
            changed = changed || this.initialSelection[i] !== this.channelSelection.selected(i);
        }
        this.changed = changed;
        this.empty = this.channelSelection.selectedCount === 0;
    }

    private updateMicrophoneInterpolationWarningStatus(): void {
        if (!this.sensorPositions || !this.uraProperties || !this.uraProperties.isUra) return;

        const spacing = this.uraProperties.spacing;

        this.interpolationWarning = false;

        // Check for each microphone if there are (non disabled) adjacent microphones
        for (let i = 0; i < this.sensorPositions.length; i++) {
            if (!this.channelSelection.selected(i)) continue;

            const mic = this.sensorPositions[i];

            let anyAdjacent = false;
            for (let i = 0; i < 3; i++) {
                if (anyAdjacent) break;

                for (let j = 0; j < 3; j++) {
                    if (anyAdjacent) break;

                    if (i === 1 && j === 1) continue;

                    const m = {
                        x: mic.x + (i - 1) * spacing,
                        y: mic.y + (j - 1) * spacing,
                        z: mic.z,
                    };

                    const index = this.sensorPositions.findIndex((x) => this.equals(x, m));
                    if (index == -1) {
                        continue;
                    }

                    anyAdjacent = !this.channelSelection.selected(index);
                }
            }

            if (!anyAdjacent) {
                // Show warning
                this.interpolationWarning = true;
                break;
            }
        }
    }

    private updateMicrophoneRmsMinMax(): void {
        if (!this.microphoneRmsValues) return;

        const minMax = { min: Number.MAX_VALUE, max: -Number.MAX_VALUE };
        for (let i = 0; i < this.microphoneRmsValues.length; i++) {
            if (!this.channelSelection.selected(i)) {
                minMax.min = Math.min(minMax.min, this.microphoneRmsValues[i]);
                minMax.max = Math.max(minMax.max, this.microphoneRmsValues[i]);
            }
        }

        this.microphoneRmsMinMax = minMax;
    }

    private equals(p1: Point3D, p2: Point3D): boolean {
        return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2) + Math.pow(p2.z - p1.z, 2)) < 1e-4;
    }

    private updateMeasurementName(newValue: string): void {
        if (!newValue) return;

        this.activatePromise = this.activatePromise.then(() => {
            this.measurementRepository
                .updateMeasurement(this.currentMeasurement, MeasurementUpdateField.measurementName, newValue)
                .then(() => {
                    this.currentMeasurement.name = newValue;
                    this.refreshMeasurements();
                })
                .catch((e) => {
                    console.error(e);
                });
        });
    }

    private updateMeasurementNotes(newValue: string): void {
        if (!newValue) return;

        this.activatePromise = this.activatePromise.then(() => {
            this.measurementRepository
                .updateMeasurement(this.currentMeasurement, MeasurementUpdateField.measurementNotes, newValue)
                .then(() => {
                    this.currentMeasurement.notes = newValue;
                })
                .catch((e) => {
                    console.error(e);
                });
        });
    }

    private updateMeasurementDistance(newValue: number): void {
        if (!newValue) return;

        this.activatePromise = this.activatePromise.then(() => {
            this.measurementRepository
                .updateMeasurement(this.currentMeasurement, MeasurementUpdateField.measurementDistance, newValue)
                .then(() => {
                    this.currentMeasurement.distance = newValue;
                })
                .catch((e) => {
                    console.error(e);
                });
        });
    }

    private updateMeasurementMetadata(index: number, newKey: string, newValue?: string): void {
        if (!newKey) return;

        this.activatePromise = this.activatePromise.then(() => {
            this.measurementRepository
                .updateMeasurement(this.currentMeasurement, MeasurementUpdateField.measurementMetadata, {
                    key: newKey,
                    value: newValue,
                })
                .then(() => {
                    if (index > this.metadata.length) this.metadata.push({ key: newKey, value: newValue });
                    else this.metadata[index] = { key: newKey, value: newValue };

                    this.addMetadata = false;
                    this.newProperty = "";
                })
                .catch((e) => {
                    console.error(e);
                });
        });
    }

    private deleteMeasurementMetadata(key: string, index: number): void {
        this.activatePromise = this.activatePromise.then(() => {
            this.measurementRepository
                .deleteMeasurementMetadata(this.currentMeasurement.id, key)
                .then(() => {
                    this.metadata.splice(index, 1);
                })
                .catch((e) => {
                    console.error(e);
                });
        });
    }

    public deleteMeasurement(): void {
        if (!this.currentMeasurement) return;

        if (this.currentMeasurement.isComputedMeasurement) {
            this.measurementRepository
                .deleteComputedMeasurement(this.currentMeasurement.id)
                .then(() => {
                    this.modalInstance.close();
                })
                .catch((e) => {
                    if (e.statusCode === 0) this.errorMessage = "Unable to communicate with the Sorama server";
                    else if (e.statusCode === 403)
                        this.errorMessage =
                            "Authorization has been denied for this request because you are not an administrator user.";
                    else this.errorMessage = e.message;
                });
        } else {
            this.measurementRepository
                .deleteMeasurement(this.currentMeasurement.id)
                .then(() => {
                    this.modalInstance.close();
                })
                .catch((e) => {
                    if (e.statusCode === 0) this.errorMessage = "Unable to communicate with the Sorama server";
                    else if (e.statusCode === 403)
                        this.errorMessage =
                            "Authorization has been denied for this request because you are not an administrator user.";
                    else this.errorMessage = e.message;
                });
        }
    }
}

// View-Model for modal needs to be first class in the file
export class MeasurementInfoModalState {
    public measurement: GenericMeasurement;
    public refreshMeasurements: () => void;
}
